"use client";

import Image from "next/image";
import { useState } from "react";
import {
  Bell,
  CalendarDays,
  Check,
  ChevronRight,
  Gift,
  LayoutGrid,
  MapPin,
  Megaphone,
  Phone,
  Plus,
  Send,
  Settings,
  Sparkles,
  Star,
  Ticket,
  TrendingDown,
  TrendingUp,
  UserRound,
  Users,
  Utensils,
} from "lucide-react";

import { createTranslator, type Translate } from "@/lib/i18n/dictionary";
import type { Locale } from "@/lib/i18n/locale";
import { useSelfDemo } from "@/components/home/mockups/use-self-demo";
import { IPHONE_17_PRO_SAFE_AREA } from "@/components/home/mockups/device-frames";
import "@/app/(site)/_i18n/product-bundles";

/**
 * The screens that go inside the product pages' device shells.
 *
 * Every one is a drawing, not a screenshot — the real product shots are not
 * cut yet — but they are drawn against the real app: the white top bar, the
 * light rail, the pastel icon tiles and the #0084EB accent are the CRM's own,
 * not an invented visual language. `DeviceNote` still marks each one on the
 * page so a reader is never shown a drawing as if it were the product.
 *
 * They are also alive, like the home page's card mock-ups. Each screen owns
 * one move — the move its section is arguing for — and `useSelfDemo` performs
 * it once the screen scrolls into view, because a still picture of a control
 * does not read as a control. Touching anything hands the screen over to the
 * reader permanently (see use-self-demo.ts).
 *
 * That makes these client components, which the pages around them are not.
 * The trade was made deliberately: the copy, the pricing table and every
 * section frame still cost the browser no JavaScript, and only the screens
 * inside the device shells hydrate.
 *
 * Sizing is in `em` throughout, like the home page's mock-ups: the enclosing
 * `DeviceStage` derives the em from its own width, so one number scales a
 * whole screen with its device and no breakpoint has to restate it. The phone
 * frames set a different em (1% of device width, see device-frames.tsx), which
 * is why the phone screens' numbers are an order of magnitude larger.
 *
 * The figures on these screens are invented. The stage marks itself
 * `data-nosnippet` (see device-frames.tsx) so they cannot be lifted into a
 * search result as if they were ours.
 */

/* ─────────────────────────── Shared furniture ─────────────────────────── */

/** Pastel icon tiles, straight off the customer profile in the real app. */
const TONE = {
  blue: "bg-[#E8F3FD] text-[#0673bb]",
  green: "bg-[#E6F6EC] text-[#1B7A3D]",
  pink: "bg-[#FDE9F3] text-[#B5327E]",
  purple: "bg-[#F0EAFB] text-[#6D45C4]",
  amber: "bg-[#FEF3E2] text-[#A45B12]",
} as const;

type Tone = keyof typeof TONE;

/**
 * The one card style every screen uses.
 *
 * A hairline *and* a shadow: the hairline is what makes a card read as a card
 * at mock-up scale, where a shadow alone is two or three physical pixels of
 * grey and disappears. The shadow is what stops the screen reading as a
 * wireframe.
 */
function Card({ className = "", children }: Readonly<{ className?: string; children: React.ReactNode }>) {
  return (
    <div
      className={`rounded-[0.5em] border border-[#EDF1F6] bg-white shadow-[0_0.12em_0.4em_-0.14em_rgba(16,40,80,0.16)] ${className}`}
    >
      {children}
    </div>
  );
}

function IconTile({ tone, className = "", children }: Readonly<{ tone: Tone; className?: string; children: React.ReactNode }>) {
  return (
    <span className={`grid shrink-0 place-items-center rounded-[0.3em] ${TONE[tone]} ${className}`}>
      {children}
    </span>
  );
}

/** The +20.4% / -3.4% chips under every figure in the real dashboard. */
function Delta({ value, up }: Readonly<{ value: string; up: boolean }>) {
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-[0.15em] rounded-full px-[0.35em] py-[0.08em] text-[0.4em] font-semibold ${
        up ? "bg-[#E6F6EC] text-[#1B7A3D]" : "bg-[#FDECEC] text-[#C2410C]"
      }`}
    >
      <Icon aria-hidden className="size-[1em]" strokeWidth={2.6} />
      {value}
    </span>
  );
}

/**
 * A segmented control that presses.
 *
 * Shared by all four screens that switch something — date range, profile tab,
 * POS category, branch — because the press is the thing that tells a reader
 * the mock-up is not a picture, and it should look identical everywhere.
 * `n` re-keys the ripple so pressing the same chip twice replays it, which the
 * self-demo's opening beat depends on (it re-presses what is already chosen).
 */
function PressChip({
  on,
  ripple,
  onClick,
  className = "",
  children,
}: Readonly<{
  on: boolean;
  ripple?: number;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`relative rounded-full px-[0.6em] py-[0.2em] text-[0.45em] font-semibold transition-colors ${
        on
          ? "bg-[#0084EB] text-white shadow-[0_0.15em_0.4em_-0.12em_rgba(0,132,235,0.65)]"
          : "bg-white text-black/55 ring-[0.06em] ring-[#E4EBF3] hover:bg-[#F2F8FE]"
      } ${className}`}
    >
      {ripple !== undefined && (
        // New element per press, which is what replays the keyframe.
        <span key={ripple} aria-hidden className="ps-mock-press pointer-events-none absolute inset-0 rounded-full" />
      )}
      {children}
    </button>
  );
}

/**
 * The app's own mark, wherever a screen draws it: the site logo rather than the
 * "J" letterform these mockups used to stand in with.
 *
 * A tinted tile instead of the solid brand-blue square that held the letter —
 * the duck is itself #0084EB, so it needs a light ground to read against.
 * `tile` carries the size and corner radius of the slot it sits in, `mark` the
 * size of the duck inside it, and `tone` the ground — overridden where the
 * mark sits on the brand-blue header rather than on a white surface.
 */
function BrandMark({
  tile,
  mark,
  tone = "bg-[#E8F3FD]",
}: Readonly<{ tile: string; mark: string; tone?: string }>) {
  return (
    <span className={`grid shrink-0 place-items-center ${tone} ${tile}`}>
      <Image src="/v2/logo.webp" alt="" width={88} height={88} className={`object-contain ${mark}`} />
    </span>
  );
}

/**
 * The app's top bar: shop switcher on the left, language / alerts / account on
 * the right, exactly as the CRM draws it.
 *
 * The EN·TH toggle is in there because it is in the product, and because it is
 * the fastest way for a reader to recognise the screenshot as a Thai SaaS
 * admin rather than a generic dashboard illustration.
 */
function TopBar({ t }: Readonly<{ t: Translate }>) {
  return (
    <div className="flex items-center justify-between border-b border-[#EDF1F6] bg-white px-[0.6em] py-[0.35em]">
      <span className="flex items-center gap-[0.3em] rounded-full bg-[#F4F7FB] py-[0.15em] pl-[0.2em] pr-[0.45em]">
        <BrandMark tile="size-[0.75em] rounded-full" mark="size-[0.5em]" />
        <span className="text-[0.45em] font-semibold text-[#16202E]">{t("product.screens.chrome.shop")}</span>
        <span className="flex items-center gap-[0.25em] rounded-full bg-[#E6F6EC] px-[0.35em] py-[0.05em] text-[0.35em] font-semibold text-[#1B7A3D]">
          <span className="size-[0.4em] rounded-full bg-[#1B7A3D]" />
          {t("product.screens.chrome.open")}
        </span>
      </span>

      <span className="flex items-center gap-[0.35em]">
        <span className="flex items-center rounded-full bg-[#F4F7FB] p-[0.08em] text-[0.35em] font-semibold">
          <span className="rounded-full px-[0.6em] py-[0.15em] text-black/45">EN</span>
          <span className="rounded-full bg-white px-[0.6em] py-[0.15em] text-[#16202E] shadow-[0_0.1em_0.3em_-0.15em_rgba(16,40,80,0.4)]">
            TH
          </span>
        </span>
        <Bell aria-hidden className="size-[0.5em] text-black/35" strokeWidth={2} />
        <span className="flex items-center gap-[0.25em]">
          <span className="text-right">
            <span className="block text-[0.4em] font-semibold leading-[1.3] text-[#16202E]">
              {t("product.screens.chrome.user")}
            </span>
            <span className="block text-[0.33em] leading-[1.3] text-black/40">
              {t("product.screens.chrome.role")}
            </span>
          </span>
          <span className="grid size-[0.8em] place-items-center rounded-full bg-[#E8F3FD]">
            <UserRound aria-hidden className="size-[0.5em] text-[#0673bb]" strokeWidth={2.2} />
          </span>
        </span>
      </span>
    </div>
  );
}

/** The CRM's left navigation, labelled — the profile screen's version. */
const CRM_NAV = [
  { key: "dashboard", Icon: LayoutGrid, tone: "blue" },
  { key: "customers", Icon: Users, tone: "green" },
  { key: "points", Icon: Ticket, tone: "purple" },
  { key: "broadcast", Icon: Megaphone, tone: "pink" },
  { key: "bookings", Icon: CalendarDays, tone: "amber" },
  { key: "reports", Icon: Sparkles, tone: "blue" },
] as const;

function SideNav({ t, at }: Readonly<{ t: Translate; at: number }>) {
  return (
    <div className="flex w-[24%] shrink-0 flex-col gap-[0.12em] border-r border-[#EDF1F6] bg-white p-[0.45em_0.35em]">
      <span className="flex items-center gap-[0.25em] px-[0.25em] pb-[0.4em]">
        <BrandMark tile="size-[0.7em] rounded-[0.2em]" mark="size-[0.48em]" />
        <span className="truncate text-[0.45em] font-bold text-[#0084EB]">
          {t("product.screens.chrome.brand")}
        </span>
      </span>

      {CRM_NAV.map(({ key, Icon, tone }, i) => (
        <span
          key={key}
          className={`flex items-center gap-[0.3em] rounded-[0.3em] px-[0.25em] py-[0.22em] ${
            i === at ? "bg-[#E8F3FD]" : ""
          }`}
        >
          <IconTile tone={tone} className={`size-[0.65em] ${i === at ? "" : "bg-transparent"}`}>
            <Icon aria-hidden className="size-[0.42em]" strokeWidth={2.2} />
          </IconTile>
          <span
            className={`truncate text-[0.4em] ${
              i === at ? "font-semibold text-[#0673bb]" : "text-black/55"
            }`}
          >
            {t(`product.screens.crmNav.${key}`)}
          </span>
        </span>
      ))}
    </div>
  );
}

/** The same navigation collapsed to icons, for the wider laptop screens. */
function IconRail({ at }: Readonly<{ at: number }>) {
  return (
    <div className="flex w-[1.8em] shrink-0 flex-col items-center gap-[0.28em] border-r border-[#EDF1F6] bg-white py-[0.5em]">
      <BrandMark tile="size-[0.8em] rounded-[0.22em]" mark="size-[0.55em]" />
      {CRM_NAV.map(({ key, Icon, tone }, i) => (
        <IconTile
          key={key}
          tone={tone}
          className={`size-[0.8em] ${i === at ? "" : "bg-transparent text-black/30"}`}
        >
          <Icon aria-hidden className="size-[0.5em]" strokeWidth={2.2} />
        </IconTile>
      ))}
      <Settings aria-hidden className="mt-auto size-[0.5em] text-black/25" strokeWidth={2} />
    </div>
  );
}

/**
 * A filled line chart, drawn from a 0–100 series.
 *
 * `preserveAspectRatio="none"` on purpose: the card that holds it is wider
 * than it is tall and the shape only has to read as a trend, so stretching the
 * viewBox is cheaper than measuring the card. `vector-effect` keeps the stroke
 * one pixel wide through that stretch.
 */
function Sparkline({ series, tint }: Readonly<{ series: number[]; tint: string }>) {
  const step = 100 / (series.length - 1);
  const line = series.map((v, i) => `${(i * step).toFixed(2)},${(100 - v).toFixed(2)}`).join(" ");

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full" aria-hidden>
      <polygon points={`0,100 ${line} 100,100`} fill={tint} fillOpacity={0.16} />
      <polyline
        points={line}
        fill="none"
        stroke={tint}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/* ────────────────────────────── CRM ────────────────────────────── */

/** The three date ranges the hero dashboard switches between. */
const RANGES = [
  {
    key: "d7",
    kpi: ["฿186,400", "1,284", "3,120", "฿1,145"],
    delta: [["+8.2%", true], ["+4.1%", true], ["+2.0%", true], ["-1.2%", false]],
    revenue: [38, 30, 52, 44, 68, 61, 82],
    repeat: [26, 34, 30, 46, 41, 58, 64],
  },
  {
    key: "m1",
    kpi: ["฿689,000", "4,500", "12,860", "฿1,293"],
    delta: [["+20.4%", true], ["-3.4%", false], ["+9.6%", true], ["+19.4%", true]],
    revenue: [30, 44, 36, 58, 50, 74, 92],
    repeat: [40, 36, 52, 48, 63, 57, 70],
  },
  {
    key: "m3",
    kpi: ["฿2,040,900", "13,720", "30,200", "฿1,262"],
    delta: [["+31.8%", true], ["+12.7%", true], ["+24.5%", true], ["-2.4%", false]],
    revenue: [22, 35, 48, 42, 66, 78, 100],
    repeat: [58, 50, 61, 44, 52, 47, 43],
  },
] as const;

/**
 * Laptop screen: the CRM overview, for the /product/crm hero.
 *
 * Its move is the date range — the control a shop owner touches first on a
 * dashboard, and the one that proves the numbers are computed rather than
 * drawn. Four figures, two deltas' directions and both charts change together.
 */
export function CrmDashboardScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [at, setAt] = useState(1);
  const [tap, setTap] = useState(0);
  const range = RANGES[at];
  const kpiKeys = ["sales", "orders", "members", "avg"] as const;

  // 7 วัน → 1 เดือน → 3 เดือน, on a loop. A single pass would leave whoever
  // arrives a moment later looking at a still, which is the thing this screen
  // is trying not to be.
  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => show(0), () => show(1), () => show(2)],
    { loop: true },
  );

  function show(i: number) {
    setAt(i);
    setTap((n) => n + 1);
  }

  function pick(i: number) {
    cancel();
    show(i);
  }

  return (
    <div ref={ref} className="flex h-full bg-[#F7F9FC] text-[#16202E]">
      <IconRail at={0} />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar t={t} />

        <div className="flex min-h-0 flex-1 flex-col gap-[0.4em] p-[0.5em_0.6em]">
          <div className="flex items-end justify-between gap-[0.5em]">
            <span>
              <span className="block text-[0.6em] font-bold leading-[1.3]">
                {t("product.screens.crmDashboard.title")}
              </span>
              <span className="block text-[0.4em] leading-[1.4] text-black/45">
                {t("product.screens.crmDashboard.sub")}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-[0.2em]">
              {RANGES.map((r, i) => (
                <PressChip key={r.key} on={i === at} ripple={i === at ? tap : undefined} onClick={() => pick(i)}>
                  {t(`product.screens.crmDashboard.ranges.${r.key}`)}
                </PressChip>
              ))}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-[0.35em]">
            {kpiKeys.map((key, i) => (
              <Card key={key} className="p-[0.4em]">
                <p className="mb-[0.15em] truncate text-[0.38em] text-black/45">
                  {t(`product.screens.crmDashboard.kpi.${key}`)}
                </p>
                {/* Re-keyed on the range so the figure pops rather than
                    silently swapping — at this scale a number that changes
                    without moving is a number nobody notices. */}
                <p key={range.key} className="ps-mock-pop text-[0.62em] font-bold leading-[1.2]">
                  {range.kpi[i]}
                </p>
                <span key={`${range.key}-d`} className="ps-mock-pop mt-[0.2em] flex items-center gap-[0.25em]">
                  <Delta value={range.delta[i][0]} up={range.delta[i][1]} />
                </span>
              </Card>
            ))}
          </div>

          {/* The assistant line the real dashboard prints above its charts. */}
          <Card className="flex items-center gap-[0.35em] border-[#D8EAFB] bg-[#F4F9FE] p-[0.35em_0.45em]">
            <IconTile tone="blue" className="size-[0.75em]">
              <Sparkles aria-hidden className="size-[0.45em]" strokeWidth={2.2} />
            </IconTile>
            <span className="min-w-0">
              <span className="block text-[0.35em] font-semibold text-[#0673bb]">
                {t("product.screens.crmDashboard.insightTitle")}
              </span>
              <span key={range.key} className="ps-mock-pop block truncate text-[0.4em] text-black/60">
                {t(`product.screens.crmDashboard.insights.${range.key}`)}
              </span>
            </span>
          </Card>

          <div className="grid min-h-0 flex-1 grid-cols-2 gap-[0.35em]">
            {([
              ["trend", "#0084EB", range.revenue],
              ["repeat", "#1B9E5F", range.repeat],
            ] as const).map(([key, tint, series]) => (
              <Card key={key} className="flex min-h-0 flex-col p-[0.4em]">
                <p className="mb-[0.25em] text-[0.38em] font-semibold text-black/50">
                  {t(`product.screens.crmDashboard.${key}`)}
                </p>
                <div key={range.key} className="ps-mock-pop min-h-0 flex-1">
                  <Sparkline series={[...series]} tint={tint} />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

type HistoryRow = { title: string; meta: string; value: string };

const PROFILE_TABS = ["orders", "points", "coupons"] as const;

const PROFILE_FIELDS = [
  { key: "code", value: "codeValue", tone: "blue", Icon: UserRound },
  { key: "phone", value: "phoneValue", tone: "green", Icon: Phone },
  { key: "birthday", value: "birthdayValue", tone: "pink", Icon: Gift },
  { key: "branch", value: "branchValue", tone: "amber", Icon: MapPin },
  { key: "tier", value: "tierValue", tone: "purple", Icon: Star },
  { key: "source", value: "sourceValue", tone: "blue", Icon: Sparkles },
] as const;

/**
 * Tablet screen: one customer's record, for the CRM deep dive.
 *
 * Its move is the activity tab. The section's claim is that every till receipt,
 * point and coupon lands on the same person, so the screen shows the same
 * profile answering three different questions rather than three screens.
 */
export function CrmProfileScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [at, setAt] = useState(0);
  const [tap, setTap] = useState(0);
  const tab = PROFILE_TABS[at];
  const rows = t(`product.screens.crmProfile.rows.${tab}`, {
    returnObjects: true,
  }) as unknown as HistoryRow[];
  const fav = t("product.screens.crmProfile.fav", { returnObjects: true }) as unknown as string[];
  const allergy = t("product.screens.crmProfile.allergy", { returnObjects: true }) as unknown as string[];

  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => show(0), () => show(1), () => show(2)],
    { loop: true },
  );

  function show(i: number) {
    setAt(i);
    setTap((n) => n + 1);
  }

  function pick(i: number) {
    cancel();
    show(i);
  }

  return (
    <div ref={ref} className="flex h-full bg-[#F7F9FC] text-[#16202E]">
      <SideNav t={t} at={1} />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar t={t} />

        <div className="flex min-h-0 flex-1 gap-[0.4em] p-[0.45em_0.5em]">
          {/* Who they are */}
          <Card className="flex w-[38%] shrink-0 flex-col gap-[0.3em] p-[0.45em]">
            <span className="flex items-start gap-[0.35em]">
              <span className="grid size-[1.3em] shrink-0 place-items-center rounded-full bg-[#E8F3FD] text-[0.6em] font-bold text-[#0673bb]">
                {t("product.screens.crmProfile.initial")}
              </span>
              <span className="min-w-0 grow">
                <span className="block truncate text-[0.52em] font-bold leading-[1.3]">
                  {t("product.screens.crmProfile.name")}
                </span>
                <span className="block truncate text-[0.35em] leading-[1.4] text-black/45">
                  {t("product.screens.crmProfile.since")}
                </span>
              </span>
            </span>

            <span className="flex flex-wrap gap-[0.2em]">
              <span className="rounded-full bg-[#E8F3FD] px-[0.4em] py-[0.1em] text-[0.35em] font-semibold text-[#0673bb]">
                {t("product.screens.crmProfile.tagCustomer")}
              </span>
              <span className="rounded-full bg-[#E6F6EC] px-[0.4em] py-[0.1em] text-[0.35em] font-semibold text-[#1B7A3D]">
                {t("product.screens.crmProfile.tagActive")}
              </span>
            </span>

            <span className="mb-[-0.1em] text-[0.36em] font-semibold text-black/45">
              {t("product.screens.crmProfile.personal")}
            </span>

            {/* Six fields fill the card on their own, so this is an ordinary
                stack. It used to be `flex-1 justify-between` with four of them,
                which spread 20px of slack into three gaps each taller than the
                rows they separated — the card read as four labels floating
                apart rather than as a record. */}
            <span className="flex flex-col gap-[0.22em]">
              {PROFILE_FIELDS.map(({ key, value, tone, Icon }) => (
                <span key={key} className="flex items-center gap-[0.3em] border-b border-[#F3F6FA] pb-[0.2em] last:border-0">
                  <IconTile tone={tone} className="size-[0.7em]">
                    <Icon aria-hidden className="size-[0.42em]" strokeWidth={2.2} />
                  </IconTile>
                  <span className="min-w-0">
                    <span className="block text-[0.32em] leading-[1.3] text-black/40">
                      {t(`product.screens.crmProfile.fields.${key}`)}
                    </span>
                    <span className="block truncate text-[0.4em] font-semibold leading-[1.35]">
                      {t(`product.screens.crmProfile.${value}`)}
                    </span>
                  </span>
                </span>
              ))}
            </span>

            {/* The two things a floor manager actually needs at the table. */}
            <span className="mt-auto flex flex-col gap-[0.2em]">
              <span className="rounded-[0.35em] bg-[#F7F9FC] p-[0.3em]">
                <span className="mb-[0.15em] block text-[0.32em] font-semibold text-black/45">
                  {t("product.screens.crmProfile.favTitle")}
                </span>
                <span className="flex flex-wrap gap-[0.15em]">
                  {fav.map((item) => (
                    <span
                      key={item}
                      className="rounded-full bg-white px-[0.35em] py-[0.08em] text-[0.32em] text-black/60 ring-[0.05em] ring-[#E4EBF3]"
                    >
                      {item}
                    </span>
                  ))}
                </span>
              </span>
              <span className="rounded-[0.35em] bg-[#FDF2F2] p-[0.3em]">
                <span className="mb-[0.15em] block text-[0.32em] font-semibold text-[#C2410C]">
                  {t("product.screens.crmProfile.allergyTitle")}
                </span>
                <span className="flex flex-wrap gap-[0.15em]">
                  {allergy.map((item) => (
                    <span
                      key={item}
                      className="rounded-full bg-white px-[0.35em] py-[0.08em] text-[0.32em] font-semibold text-[#C2410C] ring-[0.05em] ring-[#F6D2D2]"
                    >
                      {item}
                    </span>
                  ))}
                </span>
              </span>
            </span>
          </Card>

          {/* What they have done */}
          <div className="flex min-w-0 flex-1 flex-col gap-[0.35em]">
            <div className="grid grid-cols-4 gap-[0.25em]">
              {([
                ["spend", "฿8,940", ""],
                ["visits", "17", t("product.screens.crmProfile.visitsUnit")],
                ["avg", "฿526", ""],
                ["points", "420", t("product.screens.crmProfile.pointsUnit")],
              ] as const).map(([key, value, unit]) => (
                <Card key={key} className="p-[0.32em]">
                  <p className="mb-[0.1em] truncate text-[0.32em] text-black/45">
                    {t(`product.screens.crmProfile.kpi.${key}`)}
                  </p>
                  <p className="text-[0.5em] font-bold leading-[1.2]">
                    {value}
                    {unit ? <span className="text-[0.6em] font-medium text-black/40"> {unit}</span> : null}
                  </p>
                </Card>
              ))}
            </div>

            <Card className="flex min-h-0 flex-1 flex-col gap-[0.3em] p-[0.4em]">
              <span className="flex items-center justify-between gap-[0.3em]">
                <span className="truncate text-[0.42em] font-semibold">
                  {t("product.screens.crmProfile.historyTitle")}
                </span>
                <span className="flex shrink-0 items-center gap-[0.2em]">
                  {PROFILE_TABS.map((key, i) => (
                    <PressChip key={key} on={i === at} ripple={i === at ? tap : undefined} onClick={() => pick(i)}>
                      {t(`product.screens.crmProfile.tabs.${key}`)}
                    </PressChip>
                  ))}
                </span>
              </span>

              <span key={tab} className="ps-mock-pop flex flex-col gap-[0.2em]">
                {/* Keyed by position, not title: "ได้รับจากการซื้อ" is three
                    separate purchases in the points tab, and a title key made
                    them one row as far as React was concerned. */}
                {rows.map((row, i) => (
                  <span
                    key={`${row.title}-${i}`}
                    className="flex items-center gap-[0.3em] rounded-[0.3em] bg-[#F7F9FC] p-[0.34em_0.4em]"
                  >
                    <IconTile tone={tab === "orders" ? "blue" : tab === "points" ? "purple" : "amber"} className="size-[0.7em]">
                      {tab === "orders" ? (
                        <Utensils aria-hidden className="size-[0.4em]" strokeWidth={2.2} />
                      ) : tab === "points" ? (
                        <Star aria-hidden className="size-[0.4em]" strokeWidth={2.2} />
                      ) : (
                        <Ticket aria-hidden className="size-[0.4em]" strokeWidth={2.2} />
                      )}
                    </IconTile>
                    <span className="min-w-0 grow">
                      <span className="block truncate text-[0.4em] font-medium leading-[1.35]">{row.title}</span>
                      <span className="block truncate text-[0.33em] leading-[1.35] text-black/40">{row.meta}</span>
                    </span>
                    <span className="shrink-0 text-[0.4em] font-semibold">{row.value}</span>
                  </span>
                ))}
              </span>

              {/* The line the real list ends on. It also closes the card: five
                  rows leave a little room at the bottom, and a footer reads as
                  the end of a list where bare white read as a hole. */}
              <span className="mt-auto flex items-center justify-between gap-[0.3em] border-t border-[#F3F6FA] pt-[0.28em] text-[0.34em] text-black/40">
                <span className="truncate">{t("product.screens.crmProfile.viewAll")}</span>
                <ChevronRight aria-hidden className="size-[1.2em] shrink-0 text-black/25" strokeWidth={2.4} />
              </span>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Reservations ─────────────────────────── */

const FLOOR = [
  { id: "A1", state: "free", seats: 2 },
  { id: "A2", state: "booked", seats: 4 },
  { id: "A3", state: "booked", seats: 4 },
  { id: "A4", state: "free", seats: 2 },
  { id: "B1", state: "seated", seats: 6 },
  { id: "B2", state: "free", seats: 4 },
  { id: "B3", state: "booked", seats: 2 },
  { id: "B4", state: "seated", seats: 4 },
] as const;

// Saturated enough to survive the mock-up's scale: at ~450px a tablet screen
// renders these tiles about 100px wide, and a 2%-tint fill is indistinguishable
// from white there — the state, which is the whole point of a floor plan, has
// to be legible before the label is.
const FLOOR_STYLE = {
  free: "border-[#C9E9D5] bg-[#EAF7EE] text-[#1B7A3D]",
  booked: "border-[#C0E5FF] bg-[#E4F2FE] text-[#0673bb]",
  seated: "border-[#F5D9BE] bg-[#FDF1E7] text-[#A45B12]",
} as const;

/**
 * Tablet screen: tonight's floor, for the reservations deep dive.
 *
 * Its move is taking a booking: a free table is a button, pressing one turns it
 * blue, bumps the count in the footer and raises the confirmation the guest
 * gets. That is the section's whole claim — the floor and the guest's phone are
 * the same record — acted out rather than asserted.
 */
export function FloorPlanScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [taken, setTaken] = useState<string[]>([]);
  const booked = FLOOR.filter((table) => table.state === "booked").length + taken.length;
  const last = taken[taken.length - 1];

  // Book A1, book B2, then clear — the third beat is what lets the loop start
  // from the same board the first pass did instead of a full floor.
  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => setTaken(["A1"]), () => setTaken(["A1", "B2"]), () => setTaken([])],
    { loop: true, gap: 1400 },
  );

  function book(id: string) {
    cancel();
    setTaken((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }

  return (
    <div ref={ref} className="flex h-full flex-col gap-[0.4em] bg-[#F7F9FC] p-[0.6em_0.7em] text-[#16202E]">
      <div className="flex items-center justify-between">
        <span>
          <span className="block text-[0.55em] font-bold leading-[1.3]">
            {t("product.screens.floor.title")}
          </span>
          <span className="block text-[0.38em] leading-[1.4] text-black/45">
            {t("product.screens.floor.date")} · {t("product.screens.floor.hint")}
          </span>
        </span>
        <span className="rounded-full bg-[#E8F3FD] px-[0.5em] py-[0.15em] text-[0.4em] font-semibold text-[#0673bb]">
          {t("product.screens.floor.slot")}
        </span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-4 gap-[0.35em]">
        {FLOOR.map(({ id, state, seats }) => {
          const mine = taken.includes(id);
          const shown = mine ? "booked" : state;
          const free = state === "free" && !mine;

          return (
            <button
              key={id}
              type="button"
              disabled={!free}
              onClick={() => book(id)}
              className={`relative flex flex-col items-center justify-center gap-[0.1em] rounded-[0.45em] border ${FLOOR_STYLE[shown]} shadow-[0_0.1em_0.3em_-0.14em_rgba(16,40,80,0.18)] transition-colors ${
                free ? "hover:bg-[#E8F7EE]" : ""
              }`}
            >
              {mine && <span key={id} aria-hidden className="ps-mock-press pointer-events-none absolute inset-0 rounded-[0.45em]" />}
              <span className="text-[0.5em] font-bold leading-[1.2]">{id}</span>
              <span className="text-[0.33em] font-semibold leading-[1.3]">
                {t(`product.screens.floor.${shown}`)}
              </span>
              <span className="text-[0.3em] leading-[1.3] opacity-70">
                {seats} {t("product.screens.floor.seats")}
              </span>
            </button>
          );
        })}
      </div>

      {last ? (
        <Card key={last} className="ps-mock-pop flex items-center gap-[0.35em] border-[#C9E9D5] bg-[#F2FBF5] p-[0.3em_0.45em]">
          <IconTile tone="green" className="size-[0.75em] bg-[#1B7A3D] text-white">
            <Check aria-hidden className="size-[0.45em]" strokeWidth={3} />
          </IconTile>
          <span className="truncate text-[0.4em] font-medium text-[#1B7A3D]">
            {t("product.screens.floor.toast", { table: last })}
          </span>
        </Card>
      ) : (
        <Card className="flex items-center justify-between p-[0.3em_0.45em]">
          <span className="text-[0.4em] text-black/55">
            {t("product.screens.floor.summary", { count: booked })}
          </span>
          <ChevronRight aria-hidden className="size-[0.45em] text-black/25" strokeWidth={2.4} />
        </Card>
      )}
    </div>
  );
}

/**
 * The whole evening service, 17:00 to 22:30.
 *
 * Four of them are gone, which is the point: a grid where everything is free
 * is a picker, and a grid where some of it has been taken is a booking system
 * that has been running all day without anyone answering a phone. It is also
 * what fills the screen — an evening is twelve sittings, and drawing four of
 * them left two thirds of the phone empty.
 */
const SLOTS = [
  { time: "17:00", full: true },
  { time: "17:30", full: false },
  { time: "18:00", full: false },
  { time: "18:30", full: true },
  { time: "19:00", full: false },
  { time: "19:30", full: false },
  { time: "20:00", full: false },
  { time: "20:30", full: false },
  { time: "21:00", full: false },
  { time: "21:30", full: true },
  { time: "22:00", full: false },
  { time: "22:30", full: true },
] as const;

const OPEN_SLOTS = SLOTS.filter((slot) => !slot.full).length;

/**
 * Phone screen: booking a table from LINE.
 *
 * Padded off `IPHONE_17_PRO_SAFE_AREA` rather than a guessed inset — nothing
 * here draws a fake status bar, because that strip belongs to the reader's own
 * phone.
 *
 * Its move is picking a time and confirming, which is the guest's half of the
 * floor plan beside it.
 */
export function ReservationPhoneScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [at, setAt] = useState(4);
  const [done, setDone] = useState(false);
  const time = SLOTS[at].time;

  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => pickSlot(5), () => pickSlot(6), () => setDone(true)],
    { loop: true, gap: 1300 },
  );

  function pickSlot(i: number) {
    setAt(i);
    setDone(false);
  }

  function pick(i: number) {
    cancel();
    pickSlot(i);
  }

  return (
    <div ref={ref} className="flex h-full flex-col bg-[#F7F9FC] text-[#16202E]">
      {/* The brand band runs to the edges and carries the safe area itself, so
          the strip the Dynamic Island sits in is part of the page rather than
          the blank margin it used to be. */}
      <div
        className="bg-[linear-gradient(115deg,#00518D_0%,#0060A8_55%,#0A70BC_100%)] px-[5.5%] pb-[4.4em]"
        style={{ paddingTop: `calc(${IPHONE_17_PRO_SAFE_AREA.top} + 2em)` }}
      >
        <div className="flex items-center gap-[2.6em]">
          <BrandMark
            tile="size-[10em] rounded-[3.2em] shadow-[0_1.2em_2.8em_-1em_rgba(0,20,50,0.55)]"
            mark="size-[6.2em]"
            tone="bg-white"
          />
          <span className="min-w-0">
            <span className="block truncate text-[4.4em] font-bold leading-[1.25] text-white">
              {t("product.screens.bookingPhone.brand")}
            </span>
            <span className="block truncate text-[3.2em] leading-[1.35] text-white/70">
              {t("product.screens.bookingPhone.shop")}
            </span>
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[3.6em] px-[5.5%] pt-[3.6em]">
        {/* Date and party in one card split by a hairline, rather than two
            boxes: at this width two bordered cards side by side spend most of
            their room on their own edges. */}
        <div className="flex rounded-[3.4em] border border-[#EDF1F6] bg-white shadow-[0_1em_3em_-1.4em_rgba(16,40,80,0.4)]">
          {([
            ["dateLabel", "date", CalendarDays] as const,
            ["partyLabel", "party", Users] as const,
          ]).map(([label, value, Icon], i) => (
            <div
              key={label}
              className={`min-w-0 flex-1 p-[2.8em_2.8em] ${i === 1 ? "border-l border-[#EDF1F6]" : ""}`}
            >
              <span className="flex items-center gap-[0.5em] text-[2.9em] leading-[1.3] text-black/40">
                <Icon aria-hidden className="size-[1.15em] shrink-0 text-[#0673bb]" strokeWidth={2.2} />
                <span className="truncate">{t(`product.screens.bookingPhone.${label}`)}</span>
              </span>
              <p className="mt-[0.35em] truncate text-[3.7em] font-semibold leading-[1.3]">
                {t(`product.screens.bookingPhone.${value}`)}
              </p>
            </div>
          ))}
        </div>

        <div>
          <div className="mb-[2.2em] flex items-center justify-between gap-[1.4em]">
            <p className="truncate text-[3.4em] font-semibold text-black/45">
              {t("product.screens.bookingPhone.timeLabel")}
            </p>
            <span className="flex shrink-0 items-center gap-[0.5em] rounded-full bg-[#E6F6EC] px-[1em] py-[0.35em] text-[2.8em] font-semibold text-[#1B7A3D]">
              <span className="size-[0.5em] rounded-full bg-[#1B7A3D]" />
              {t("product.screens.bookingPhone.openSlots", { count: OPEN_SLOTS })}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-[1.8em]">
            {SLOTS.map((slot, i) => (
              <button
                key={slot.time}
                type="button"
                onClick={() => pick(i)}
                disabled={slot.full}
                aria-pressed={i === at}
                className={`relative rounded-[2.4em] py-[2.4em] text-[3.2em] font-semibold transition-colors ${
                  slot.full
                    ? "border border-[#EDF1F6] bg-[#F1F4F8] text-black/25 line-through"
                    : i === at
                      ? "bg-[#0084EB] text-white shadow-[0_1.2em_3em_-1.2em_rgba(0,132,235,0.9)]"
                      : "border border-[#E4EBF3] bg-white text-black/55"
                }`}
              >
                {i === at && !slot.full && (
                  <span key={slot.time} aria-hidden className="ps-mock-press pointer-events-none absolute inset-0 rounded-[2.4em]" />
                )}
                {slot.time}
              </button>
            ))}
          </div>
        </div>

        {/* Which table the pick actually reserves — the same board the tablet
            beside this phone is showing, so the two devices read as one record
            rather than two illustrations. */}
        <div className="flex items-center gap-[2.2em] rounded-[3.4em] border border-[#D8EAFB] bg-[#F4F9FE] p-[2.6em_2.8em]">
          <span className="grid size-[8em] shrink-0 place-items-center rounded-[2.6em] bg-white text-[#0673bb] shadow-[0_0.8em_2em_-1em_rgba(16,40,80,0.35)]">
            <MapPin aria-hidden className="size-[4.4em]" strokeWidth={2.2} />
          </span>
          <span className="min-w-0">
            <span className="block text-[2.9em] leading-[1.3] text-black/40">
              {t("product.screens.bookingPhone.tableLabel")}
            </span>
            <span className="block truncate text-[3.5em] font-semibold leading-[1.3] text-[#0673bb]">
              {t("product.screens.bookingPhone.table")}
            </span>
          </span>
        </div>
      </div>

      {/* An action bar rather than a button floating in the page: whatever
          height is left over ends up above a white bar with an edge on it,
          which reads as the app's own furniture instead of as a hole. */}
      <div
        className="border-t border-[#E7ECF3] bg-white px-[5.5%] pt-[3em]"
        style={{ paddingBottom: `calc(${IPHONE_17_PRO_SAFE_AREA.bottom} + 1.6em)` }}
      >
        {done ? (
          <div
            key={time}
            className="ps-mock-pop flex items-center gap-[2.2em] rounded-[3.4em] border border-[#C9E9D5] bg-[#F2FBF5] p-[2.6em_2.8em]"
          >
            <span className="grid size-[8em] shrink-0 place-items-center rounded-full bg-[#1B7A3D] text-white">
              <Check aria-hidden className="size-[4.8em]" strokeWidth={3} />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[3.4em] font-bold leading-[1.3] text-[#1B7A3D]">
                {t("product.screens.bookingPhone.doneTitle")}
              </span>
              <span className="block truncate text-[2.9em] leading-[1.35] text-[#1B7A3D]/75">
                {t("product.screens.bookingPhone.doneBody", { time })}
              </span>
              <span className="block truncate text-[2.7em] leading-[1.35] text-black/40">
                {t("product.screens.bookingPhone.reminder")}
              </span>
            </span>
          </div>
        ) : (
          <>
            {/* The section's headline is "without anyone picking up"; this is
                that promise where the guest actually reads it. */}
            <p className="mb-[2em] flex items-center justify-center gap-[0.6em] text-center text-[2.8em] leading-[1.35] text-black/40">
              <Check aria-hidden className="size-[1.2em] shrink-0 text-[#1B7A3D]" strokeWidth={3} />
              {t("product.screens.bookingPhone.autoConfirm")}
            </p>
            <button
              type="button"
              onClick={() => {
                cancel();
                setDone(true);
              }}
              className="block w-full rounded-[3.2em] bg-[#0084EB] p-[3em] text-center text-[3.7em] font-semibold text-white shadow-[0_1.4em_3.4em_-1.4em_rgba(0,132,235,0.9)]"
            >
              {t("product.screens.bookingPhone.cta", { time })}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────── POS ────────────────────────────── */

/**
 * The menu both till screens sell from.
 *
 * `art` is the tint the tile fills with while the photograph loads and behind
 * its transparent edges — the colours the swatches used to be, kept because a
 * grey box flashing in a six-up grid is more distracting than a warm one.
 */
const MENU = [
  { key: "kaprao", price: 75, img: "/v2/menu/kaprao.webp", art: "#F3D9BE" },
  { key: "friedRice", price: 70, img: "/v2/menu/friedRice.webp", art: "#E3E9F2" },
  { key: "tea", price: 45, img: "/v2/menu/tea.webp", art: "#D9C1A6" },
  { key: "tomYum", price: 120, img: "/v2/menu/tomYum.webp", art: "#E7F0DC" },
  { key: "porkBelly", price: 90, img: "/v2/menu/porkBelly.webp", art: "#F4E3E3" },
  { key: "bingsu", price: 150, img: "/v2/menu/bingsu.webp", art: "#E9E3F4" },
] as const;

const baht = (n: number) => `฿${n.toLocaleString("en-US")}`;

/**
 * Tablet screen: taking an order, for the /product/pos hero.
 *
 * Its move is the one a till does all day: tap a dish, it lands on the ticket,
 * the total moves. The newest line carries `ps-mock-land` — the same arriving
 * flash the home page's POS card uses — so the tap and its consequence are
 * visibly the same event even though they are on opposite sides of the screen.
 */
export function PosOrderScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [lines, setLines] = useState<{ key: string; qty: number }[]>([
    { key: "kaprao", qty: 2 },
  ]);
  const [sent, setSent] = useState(false);
  const total = lines.reduce(
    (sum, line) => sum + line.qty * (MENU.find((m) => m.key === line.key)?.price ?? 0),
    0,
  );
  const newest = lines[lines.length - 1]?.key;

  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [
      () => add("tomYum"),
      () => add("tea"),
      () => setSent(true),
      () => {
        setLines([{ key: "kaprao", qty: 2 }]);
        setSent(false);
      },
    ],
    { loop: true, gap: 1250 },
  );

  function add(key: string) {
    setSent(false);
    setLines((prev) => {
      const hit = prev.find((line) => line.key === key);
      if (hit) return prev.map((line) => (line.key === key ? { ...line, qty: line.qty + 1 } : line));
      return [...prev, { key, qty: 1 }];
    });
  }

  function tap(key: string) {
    cancel();
    add(key);
  }

  return (
    <div ref={ref} className="grid h-full grid-cols-[1fr_0.46fr] bg-[#F7F9FC] text-[#16202E]">
      <div className="flex min-w-0 flex-col gap-[0.4em] p-[0.55em]">
        <div className="flex items-center gap-[0.25em]">
          {(["recommended", "mains", "drinks", "desserts"] as const).map((key, i) => (
            <span
              key={key}
              className={`rounded-full px-[0.6em] py-[0.2em] text-[0.42em] font-semibold ${
                i === 0
                  ? "bg-[#0084EB] text-white shadow-[0_0.15em_0.4em_-0.12em_rgba(0,132,235,0.65)]"
                  : "bg-white text-black/55 ring-[0.06em] ring-[#E4EBF3]"
              }`}
            >
              {t(`product.screens.pos.cat.${key}`)}
            </span>
          ))}
          <span className="ml-auto text-[0.36em] text-black/35">{t("product.screens.pos.hint")}</span>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-3 gap-[0.4em]">
          {MENU.map(({ key, price, img, art }) => (
            <button
              key={key}
              type="button"
              onClick={() => tap(key)}
              className="group relative flex flex-col gap-[0.25em] overflow-hidden rounded-[0.5em] border border-[#EDF1F6] bg-white p-[0.3em] text-left shadow-[0_0.12em_0.4em_-0.14em_rgba(16,40,80,0.16)] transition-transform hover:-translate-y-[0.05em]"
            >
              {/* `relative` + `fill` rather than width/height: the tile takes
                  whatever height the grid row leaves, which is a number no
                  `em` here knows. */}
              <span
                className="relative min-h-0 flex-1 overflow-hidden rounded-[0.35em]"
                style={{ background: art }}
              >
                <Image
                  src={img}
                  alt=""
                  fill
                  sizes="120px"
                  className="object-cover"
                />
              </span>
              <span className="block truncate text-[0.4em] font-semibold leading-[1.3]">
                {t(`product.screens.pos.menu.${key}`)}
              </span>
              <span className="flex items-center justify-between">
                <span className="text-[0.4em] font-bold text-[#0673bb]">{baht(price)}</span>
                <span className="grid size-[0.75em] place-items-center rounded-full bg-[#E8F3FD] text-[#0673bb]">
                  <Plus aria-hidden className="size-[0.45em]" strokeWidth={3} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-[0.35em] border-l border-[#EDF1F6] bg-white p-[0.55em_0.5em]">
        <div className="flex items-center justify-between">
          <span className="text-[0.55em] font-bold">{t("product.screens.pos.table")}</span>
          <span className="rounded-full bg-[#E8F3FD] px-[0.45em] py-[0.12em] text-[0.36em] font-semibold text-[#0673bb]">
            {t("product.screens.pos.covers")}
          </span>
        </div>

        <p className="text-[0.36em] font-semibold text-black/40">{t("product.screens.pos.orderTitle")}</p>

        <div className="flex min-h-0 flex-1 flex-col gap-[0.22em]">
          {lines.map((line) => (
            <span
              // Re-keyed on quantity so bumping an existing line replays the
              // arrival flash — otherwise a second tap on the same dish looks
              // like nothing happened.
              key={`${line.key}-${line.qty}`}
              className={`flex items-center justify-between gap-[0.3em] rounded-[0.3em] bg-[#F7F9FC] p-[0.25em_0.32em] ${
                line.key === newest ? "ps-mock-land" : ""
              }`}
            >
              <span className="flex min-w-0 items-center gap-[0.25em]">
                <span className="grid size-[0.65em] shrink-0 place-items-center rounded-[0.2em] bg-white text-[0.32em] font-bold text-[#0673bb] ring-[0.05em] ring-[#E4EBF3]">
                  {line.qty}
                </span>
                <span className="truncate text-[0.38em] leading-[1.35] text-black/75">
                  {t(`product.screens.pos.menu.${line.key}`)}
                </span>
              </span>
              <span className="shrink-0 text-[0.38em] font-semibold">
                {baht(line.qty * (MENU.find((m) => m.key === line.key)?.price ?? 0))}
              </span>
            </span>
          ))}
        </div>

        <div className="h-px bg-[#EDF1F6]" />
        <div className="flex items-baseline justify-between">
          <span className="text-[0.42em] text-black/50">{t("product.screens.pos.total")}</span>
          <span key={total} className="ps-mock-pop text-[0.75em] font-bold">
            {baht(total)}
          </span>
        </div>

        {sent ? (
          <span className="ps-mock-pop flex items-center justify-center gap-[0.25em] rounded-[0.4em] bg-[#E6F6EC] py-[0.3em] text-[0.4em] font-semibold text-[#1B7A3D]">
            <Check aria-hidden className="size-[1em]" strokeWidth={3.2} />
            {t("product.screens.pos.sentToKitchen")}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              cancel();
              setSent(true);
            }}
            className="flex items-center justify-center gap-[0.25em] rounded-[0.4em] bg-[#0084EB] py-[0.3em] text-[0.4em] font-semibold text-white shadow-[0_0.15em_0.4em_-0.12em_rgba(0,132,235,0.65)]"
          >
            <Send aria-hidden className="size-[1em]" strokeWidth={2.4} />
            {t("product.screens.pos.send")}
          </button>
        )}
      </div>
    </div>
  );
}

/** The four things the branch dashboard switches between. */
const BRANCHES = [
  {
    key: "all",
    kpi: ["฿86,420", "214", "฿404", "6"],
    hours: [22, 30, 58, 84, 66, 40, 52, 92, 100, 61],
    peak: "19:00",
  },
  {
    key: "thonglor",
    kpi: ["฿41,180", "96", "฿429", "2"],
    hours: [18, 26, 44, 70, 58, 36, 62, 88, 100, 74],
    peak: "19:00",
  },
  {
    key: "siam",
    kpi: ["฿28,640", "78", "฿367", "3"],
    hours: [34, 52, 88, 100, 62, 30, 38, 66, 74, 41],
    peak: "12:00",
  },
  {
    key: "ari",
    kpi: ["฿16,600", "40", "฿415", "1"],
    hours: [12, 20, 33, 48, 40, 28, 46, 72, 100, 88],
    peak: "20:00",
  },
] as const;

/**
 * Laptop screen: takings across every branch, for the POS deep dive.
 *
 * Its move is the branch switch, which is the section's argument in one
 * control: the same figures, one branch at a time or all of them at once,
 * without exporting anything. Siam's peak is at noon and Ari's at eight on
 * purpose — a picker whose branches all look the same makes no case.
 */
export function BranchDashboardScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [at, setAt] = useState(0);
  const [tap, setTap] = useState(0);
  const branch = BRANCHES[at];
  const kpiKeys = ["sales", "bills", "perBill", "lowStock"] as const;

  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => show(1), () => show(2), () => show(3), () => show(0)],
    { loop: true },
  );

  function show(i: number) {
    setAt(i);
    setTap((n) => n + 1);
  }

  function pick(i: number) {
    cancel();
    show(i);
  }

  return (
    <div ref={ref} className="flex h-full flex-col gap-[0.4em] bg-[#F7F9FC] p-[0.5em_0.6em] text-[#16202E]">
      <div className="flex items-end justify-between gap-[0.5em]">
        <span>
          <span className="block text-[0.55em] font-bold leading-[1.3]">
            {t("product.screens.branch.title")}
          </span>
          <span className="flex items-center gap-[0.2em] text-[0.38em] leading-[1.4] text-black/45">
            <span className="size-[0.4em] rounded-full bg-[#1B7A3D]" />
            {t("product.screens.branch.today")}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-[0.2em]">
          {BRANCHES.map((b, i) => (
            <PressChip key={b.key} on={i === at} ripple={i === at ? tap : undefined} onClick={() => pick(i)}>
              {b.key === "all"
                ? t("product.screens.branch.all")
                : t(`product.screens.branch.branches.${b.key}`)}
            </PressChip>
          ))}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-[0.35em]">
        {kpiKeys.map((key, i) => (
          <Card key={key} className="p-[0.4em]">
            <p className="mb-[0.15em] truncate text-[0.38em] text-black/45">
              {t(`product.screens.branch.${key}`)}
            </p>
            <p
              key={branch.key}
              className={`ps-mock-pop text-[0.62em] font-bold leading-[1.2] ${
                key === "lowStock" ? "text-[#C2410C]" : ""
              }`}
            >
              {branch.kpi[i]}
              {key === "lowStock" ? (
                <span className="text-[0.55em] font-medium text-black/40">
                  {" "}
                  {t("product.screens.branch.lowStockUnit")}
                </span>
              ) : null}
            </p>
          </Card>
        ))}
      </div>

      <Card className="flex min-h-0 flex-1 flex-col p-[0.45em_0.5em]">
        <div className="mb-[0.35em] flex items-center justify-between">
          <p className="text-[0.4em] font-semibold text-black/50">{t("product.screens.branch.chart")}</p>
          <p key={branch.key} className="ps-mock-pop text-[0.36em] font-semibold text-[#0673bb]">
            {t("product.screens.branch.peak", { hour: branch.peak })}
          </p>
        </div>
        <div key={branch.key} className="ps-mock-pop flex min-h-0 flex-1 items-end gap-[0.25em]">
          {branch.hours.map((h, i) => (
            <span
              key={i}
              style={{ height: `${h}%` }}
              className={`grow rounded-t-[0.12em] ${
                h >= 88 ? "bg-[#0084EB]" : h >= 52 ? "bg-[#8CC5EC]" : "bg-[#D3E8F8]"
              }`}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

/** The category rail the guest's menu opens on. */
const SELF_CATS = ["recommended", "mains", "drinks", "desserts"] as const;

/**
 * Phone screen: the customer's own menu, reached by scanning the table's QR.
 *
 * Its move is the guest adding to the cart — the same `add` the POS screen does
 * from the other side of the counter, which is the point of putting the two
 * devices in one section.
 */
export function SelfOrderPhoneScreen({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);
  const [cart, setCart] = useState<Record<string, number>>({});
  const count = Object.values(cart).reduce((sum, n) => sum + n, 0);
  const total = Object.entries(cart).reduce(
    (sum, [key, n]) => sum + n * (MENU.find((m) => m.key === key)?.price ?? 0),
    0,
  );

  const { ref, cancel } = useSelfDemo<HTMLDivElement>(
    [() => add("kaprao"), () => add("tea"), () => setCart({})],
    { loop: true, gap: 1300 },
  );

  function add(key: string) {
    setCart((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  }

  function tap(key: string) {
    cancel();
    add(key);
  }

  return (
    <div ref={ref} className="flex h-full flex-col bg-[#F7F9FC] text-[#16202E]">
      <div
        className="px-[4.5%] pb-[2.6em]"
        style={{ paddingTop: `calc(${IPHONE_17_PRO_SAFE_AREA.top} + 1.4em)` }}
      >
        <span className="inline-flex items-center rounded-full bg-[#E8F3FD] px-[2.4em] py-[1em] text-[2.7em] font-semibold text-[#0673bb]">
          {t("product.screens.selfOrder.table")}
        </span>
        <p className="mt-[0.5em] text-[4.4em] font-bold leading-[1.25]">
          {t("product.screens.selfOrder.title")}
        </p>
        <p className="text-[2.9em] text-black/45">{t("product.screens.selfOrder.shop")}</p>
      </div>

      {/* The category rail, clipped at the right edge rather than wrapped —
          a row that runs off the screen is how a phone says "there is more
          sideways", and wrapping it to two lines would say the opposite. */}
      <div className="flex gap-[1.6em] overflow-hidden px-[4.5%] pb-[2.8em]">
        {SELF_CATS.map((key, i) => (
          <span
            key={key}
            className={`shrink-0 whitespace-nowrap rounded-full px-[1.5em] py-[0.8em] text-[2.7em] font-semibold ${
              i === 0
                ? "bg-[#0084EB] text-white"
                : "border border-[#E4EBF3] bg-white text-black/50"
            }`}
          >
            {t(`product.screens.pos.cat.${key}`)}
          </span>
        ))}
      </div>

      {/* Cards with the dish on top, two across — a list of thin rows left two
          thirds of the phone empty, and is not what a menu the guest orders
          from actually looks like. */}
      <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-[2.2em] px-[4.5%] pb-[2.6em]">
        {MENU.map(({ key, price, img, art }) => {
          const qty = cart[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => tap(key)}
              className="overflow-hidden rounded-[3em] border border-[#EDF1F6] bg-white text-left shadow-[0_1em_3em_-1.4em_rgba(16,40,80,0.4)]"
            >
              <span
                className="relative block h-[26em] w-full overflow-hidden"
                style={{ background: art }}
              >
                <Image src={img} alt="" fill sizes="90px" className="object-cover" />
              </span>
              <span className="block p-[1.8em_2em_2em]">
                <span className="block truncate text-[2.9em] font-semibold leading-[1.35]">
                  {t(`product.screens.pos.menu.${key}`)}
                </span>
                <span className="mt-[0.9em] flex items-center justify-between gap-[1em]">
                  <span className="truncate text-[3.1em] font-bold leading-[1.2] text-[#0673bb]">
                    {baht(price)}
                  </span>
                  {qty > 0 ? (
                    <span
                      key={qty}
                      className="ps-mock-pop grid size-[6em] shrink-0 place-items-center rounded-[2em] bg-[#E6F6EC] font-bold text-[#1B7A3D]"
                    >
                      {/* The count carries its own font-size rather than the
                          chip: `size-[6em]` next to `text-[3em]` on one element
                          is six of *that* font's ems, so the chip came out
                          three times the plus button and overflowed the card. */}
                      <span className="text-[3em] leading-none">{qty}</span>
                    </span>
                  ) : (
                    <span className="grid size-[6em] shrink-0 place-items-center rounded-[2em] bg-[#0084EB] text-white">
                      <Plus aria-hidden className="size-[3.4em]" strokeWidth={3} />
                    </span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The cart bar, on the app's own furniture rather than floating in the
          page — same move as the booking phone's footer. */}
      <div
        className="border-t border-[#E7ECF3] bg-white px-[4.5%] pt-[2.6em]"
        style={{ paddingBottom: `calc(${IPHONE_17_PRO_SAFE_AREA.bottom} + 1.4em)` }}
      >
        {count > 0 ? (
          <span
            key={count}
            className="ps-mock-pop flex items-center justify-between rounded-[3.2em] bg-[#0084EB] p-[2.8em_3.4em] text-[3.2em] font-semibold text-white shadow-[0_1.4em_3.4em_-1.4em_rgba(0,132,235,0.9)]"
          >
            <span className="flex items-center gap-[0.5em]">
              <Gift aria-hidden className="size-[1.1em]" strokeWidth={2.4} />
              {t("product.screens.selfOrder.cart", { count })}
            </span>
            <span>{baht(total)}</span>
          </span>
        ) : (
          <span className="flex items-center justify-center rounded-[3.2em] border border-[#E4EBF3] bg-[#F7F9FC] p-[2.8em] text-[3.1em] font-medium text-black/35">
            {t("product.screens.selfOrder.cartEmpty")}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The QR the table card prints, as a real scannable code for the site's own
 * home page — 25x25 modules, version 2 at error-correction level M, drawn as
 * one merged path so it stays crisp at any size.
 *
 * It used to be a decorative lattice, on the reasoning that a working code
 * would send whoever scanned it somewhere we had not chosen. Pointing it at
 * jelloducks.com settles that: it is our page, and a marketing shot of a
 * self-order QR that does not resolve is a prop rather than a product.
 *
 * Regenerate after a URL change with:
 *   python3 -c "import segno;q=segno.make('https://jelloducks.com/',error='m');
 *   ..."  # merge each row's dark runs into M{x} {y}h{n}v1h-{n}z
 */
const QR_PATH =
  "M0 0h7v1h-7zM8 0h3v1h-3zM12 0h2v1h-2zM18 0h7v1h-7zM0 1h1v1h-1zM6 1h1v1h-1zM8 1h2v1h-2zM14 1h1v1h-1zM16 1h1v1h-1zM18 1h1v1h-1zM24 1h1v1h-1zM0 2h1v1h-1zM2 2h3v1h-3zM6 2h1v1h-1zM8 2h2v1h-2zM12 2h3v1h-3zM16 2h1v1h-1zM18 2h1v1h-1zM20 2h3v1h-3zM24 2h1v1h-1zM0 3h1v1h-1zM2 3h3v1h-3zM6 3h1v1h-1zM9 3h8v1h-8zM18 3h1v1h-1zM20 3h3v1h-3zM24 3h1v1h-1zM0 4h1v1h-1zM2 4h3v1h-3zM6 4h1v1h-1zM8 4h1v1h-1zM10 4h5v1h-5zM16 4h1v1h-1zM18 4h1v1h-1zM20 4h3v1h-3zM24 4h1v1h-1zM0 5h1v1h-1zM6 5h1v1h-1zM9 5h2v1h-2zM12 5h3v1h-3zM18 5h1v1h-1zM24 5h1v1h-1zM0 6h7v1h-7zM8 6h1v1h-1zM10 6h1v1h-1zM12 6h1v1h-1zM14 6h1v1h-1zM16 6h1v1h-1zM18 6h7v1h-7zM9 7h3v1h-3zM13 7h3v1h-3zM0 8h1v1h-1zM3 8h9v1h-9zM14 8h4v1h-4zM20 8h1v1h-1zM22 8h3v1h-3zM0 9h1v1h-1zM4 9h2v1h-2zM7 9h2v1h-2zM12 9h1v1h-1zM14 9h2v1h-2zM19 9h5v1h-5zM1 10h2v1h-2zM5 10h2v1h-2zM8 10h1v1h-1zM10 10h2v1h-2zM13 10h4v1h-4zM18 10h1v1h-1zM21 10h1v1h-1zM24 10h1v1h-1zM0 11h2v1h-2zM3 11h2v1h-2zM7 11h1v1h-1zM11 11h2v1h-2zM14 11h1v1h-1zM18 11h1v1h-1zM20 11h5v1h-5zM0 12h1v1h-1zM3 12h4v1h-4zM9 12h2v1h-2zM15 12h1v1h-1zM18 12h2v1h-2zM24 12h1v1h-1zM0 13h1v1h-1zM3 13h3v1h-3zM7 13h2v1h-2zM12 13h1v1h-1zM14 13h3v1h-3zM20 13h1v1h-1zM23 13h1v1h-1zM0 14h2v1h-2zM6 14h1v1h-1zM8 14h1v1h-1zM14 14h4v1h-4zM20 14h5v1h-5zM0 15h1v1h-1zM3 15h1v1h-1zM7 15h2v1h-2zM10 15h1v1h-1zM12 15h1v1h-1zM18 15h2v1h-2zM21 15h2v1h-2zM24 15h1v1h-1zM0 16h1v1h-1zM6 16h8v1h-8zM15 16h6v1h-6zM22 16h2v1h-2zM8 17h2v1h-2zM11 17h1v1h-1zM13 17h1v1h-1zM16 17h1v1h-1zM20 17h1v1h-1zM22 17h2v1h-2zM0 18h7v1h-7zM8 18h3v1h-3zM12 18h2v1h-2zM16 18h1v1h-1zM18 18h1v1h-1zM20 18h1v1h-1zM24 18h1v1h-1zM0 19h1v1h-1zM6 19h1v1h-1zM8 19h4v1h-4zM13 19h1v1h-1zM15 19h2v1h-2zM20 19h1v1h-1zM23 19h1v1h-1zM0 20h1v1h-1zM2 20h3v1h-3zM6 20h1v1h-1zM8 20h1v1h-1zM10 20h3v1h-3zM15 20h6v1h-6zM23 20h2v1h-2zM0 21h1v1h-1zM2 21h3v1h-3zM6 21h1v1h-1zM8 21h1v1h-1zM10 21h1v1h-1zM12 21h4v1h-4zM17 21h2v1h-2zM23 21h2v1h-2zM0 22h1v1h-1zM2 22h3v1h-3zM6 22h1v1h-1zM10 22h4v1h-4zM16 22h2v1h-2zM20 22h5v1h-5zM0 23h1v1h-1zM6 23h1v1h-1zM10 23h3v1h-3zM14 23h1v1h-1zM19 23h2v1h-2zM22 23h3v1h-3zM0 24h7v1h-7zM8 24h1v1h-1zM11 24h4v1h-4zM16 24h3v1h-3zM21 24h1v1h-1zM24 24h1v1h-1z";

/**
 * The table's QR card — a printed thing, not a screen, so it stands beside the
 * phone rather than inside a device, and is the one mock-up here with nothing
 * to press. Its sizes are in px for the same reason: no  derives
 * an em for it.
 */
export function QrTableCard({ locale }: Readonly<{ locale: Locale }>) {
  const t = createTranslator(locale);

  return (
    <div className="flex w-[176px] flex-col items-center gap-2.5 rounded-2xl border border-[#E6E6E6] bg-white p-[20px_16px] shadow-[0_10px_28px_-12px_rgba(16,40,80,0.35)]">
      <BrandMark tile="size-7 rounded-lg" mark="size-5" />
      <p className="text-[13px]/[20px] font-semibold text-[#16202E]">
        {t("product.screens.selfOrder.qrTitle")}
      </p>
      {/* The viewBox carries a 3-module quiet zone of its own, so the code
          keeps its margin whatever the card's padding does. `crispEdges` stops
          the browser antialiasing module edges into grey, which is what a
          scanner reads as noise. */}
      <svg
        viewBox="-3 -3 31 31"
        className="size-[128px]"
        shapeRendering="crispEdges"
        role="img"
        aria-label={t("product.screens.selfOrder.qrTitle")}
      >
        <rect x={-3} y={-3} width={31} height={31} fill="#FFFFFF" />
        <path d={QR_PATH} fill="#16202E" />
      </svg>
      <p className="text-center text-[12px]/[18px] text-black/50">
        {t("product.screens.selfOrder.qrSub")}
      </p>
    </div>
  );
}
